import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Collapse,
  Stack,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { CategoriaWithSubcategorias } from '../types/categoria.types';

interface CategoriaTreeProps {
  categorias: CategoriaWithSubcategorias[];
  loading?: boolean;
  error?: string | null;
  onEditCategoria: (cat: CategoriaWithSubcategorias) => void;
  onDeleteCategoria: (id: number) => void;
  onAddSubcategoria: (categoriaId: number) => void;
  onEditSubcategoria: (categoriaId: number, subId: number) => void;
  onDeleteSubcategoria: (categoriaId: number, subId: number) => void;
}

export function CategoriaTree({
  categorias,
  loading = false,
  error,
  onEditCategoria,
  onDeleteCategoria,
  onAddSubcategoria,
  onEditSubcategoria,
  onDeleteSubcategoria,
}: CategoriaTreeProps) {
  const [expandedCategorias, setExpandedCategorias] = useState<Set<number>>(new Set());
  const [deleteDialog, setDeleteDialog] = useState<{ type: 'categoria' | 'subcategoria'; id: number; parentId?: number } | null>(null);

  const toggleCategoria = (id: number) => {
    const newExpanded = new Set(expandedCategorias);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCategorias(newExpanded);
  };

  const handleDeleteClick = (type: 'categoria' | 'subcategoria', id: number, parentId?: number) => {
    setDeleteDialog({ type, id, parentId });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog) return;

    try {
      if (deleteDialog.type === 'categoria') {
        onDeleteCategoria(deleteDialog.id);
      } else {
        onDeleteSubcategoria(deleteDialog.parentId!, deleteDialog.id);
      }
      setDeleteDialog(null);
    } catch (err) {
      // Error handling is done at parent level
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (categorias.length === 0) {
    return <Typography color="textSecondary">No hay categorías</Typography>;
  }

  return (
    <>
      <Stack spacing={1}>
        {categorias.map((categoria) => (
          <Box key={categoria.id}>
            {/* Categoría */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <IconButton
                size="small"
                onClick={() => toggleCategoria(categoria.id)}
                sx={{ visibility: categoria.subcategorias?.length > 0 ? 'visible' : 'hidden' }}
              >
                {expandedCategorias.has(categoria.id) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>

              {/* Icono y nombre */}
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  minWidth: '2rem',
                  textAlign: 'center',
                }}
              >
                {categoria.icono}
              </Typography>
              <Typography
                sx={{
                  flex: 1,
                  fontWeight: 500,
                  color: categoria.color || 'inherit',
                }}
              >
                {categoria.nombre}
              </Typography>

              {/* Acciones */}
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => onEditCategoria(categoria)}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick('categoria', categoria.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Subcategorías */}
            <Collapse in={expandedCategorias.has(categoria.id)} timeout="auto">
              <Box sx={{ pl: 4 }}>
                {categoria.subcategorias?.map((subcategoria) => (
                  <Box
                    key={subcategoria.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { backgroundColor: '#f9f9f9' },
                    }}
                  >
                    <Typography sx={{ flex: 1 }}>
                      {subcategoria.nombre}
                      <Typography
                        variant="caption"
                        sx={{ ml: 1, color: 'textSecondary' }}
                      >
                        ({subcategoria.productosCount || 0} productos)
                      </Typography>
                    </Typography>

                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => onEditSubcategoria(categoria.id, subcategoria.id)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick('subcategoria', subcategoria.id, categoria.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}

                {/* Botón agregar subcategoría */}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onAddSubcategoria(categoria.id)}
                  sx={{ mt: 1, ml: 2 }}
                >
                  Agregar Subcategoría
                </Button>
              </Box>
            </Collapse>
          </Box>
        ))}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar esto? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
